import type { Locale, SectionKey } from "../../shared/dto";
import { intlLanguage } from "../date-format";
import { RAIL_STEP_KEYS } from "../rail-model";
import type {
  StaffDetail,
  StaffRailStep,
  StaffStepStatus
} from "./model";

export function confirmForClient(
  props: { detail: StaffDetail; t: (key: string) => string },
  messageKey: string
): boolean {
  return window.confirm(`${props.t(messageKey)} ${props.detail.client.name}`);
}

export function toDateTimeLocal(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
}

export function formatDate(value: string, locale: Locale): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat(intlLanguage(locale), {
        dateStyle: "short",
        timeStyle: "short"
      }).format(date)
    : value;
}

export function makeSafeT(
  t: (key: string) => string
): (key: string, fallback: string) => string {
  return (key, fallback) => {
    try {
      return t(key);
    } catch {
      return fallback;
    }
  };
}

export function phaseLabel(key: SectionKey): string {
  return key.replaceAll("_", " ");
}

/**
 * F126 (Pavel 2026-08-11): where the client HAS GOT TO, which is not the same
 * question the client's own "next step" pointer answers.
 *
 * "V dashboardu partaka to bile kolecko postupu mi zustava uviznute na prvnim
 * modrem kolecku - zpetna vazba zustava otevrena az do konce, takze se kolecko
 * neposouva dal. Krasne je to videt na klientovi Pavlu Rihovi."
 *
 * The pointer this used to read (`primaryNext`, server-side
 * `PRIMARY_NEXT_LADDER`) picks the FIRST section still on the client's turn.
 * Beta and feedback stay on the client's turn until the handover closes them
 * (F101), and both sit early in that ladder - so for every client who ever
 * opened their beta, the ring parked there and never moved again while the
 * sales chapter ran. That pointer is right for the CLIENT ("what do I do
 * next"); it is the wrong question for the partner, who is asking "how far is
 * this client".
 *
 * 🔴 The client's pointer is untouched. This is the partner's own reading, and
 * it is deliberately the ONE definition behind the matrix ring, the "Cesta
 * klienta" ring, the status card and the hero, so one screen cannot show the
 * ring on step 12 and the title on step 6.
 *
 * The rule, in journey order:
 *   1. how far the journey REACHES: walk from step 1 and stop at the first
 *      locked step. Everything before it is where this client has been able
 *      to go at all.
 *   2. inside that reach, find the furthest step that really ADVANCED -
 *      finished, being worked on, or stuck. Merely being unlocked is not
 *      advancing.
 *   3. the ring goes on the first step still OPEN from there on: work has
 *      got this far, and this is the first thing not closed at that point.
 *   4. nothing open past it: the last finished step - the end of the journey
 *      so far.
 *   5. nothing reached at all: the first step, so a brand new client reads as
 *      standing at the beginning rather than at the end.
 *
 * 🔴 Step 2 is why this is not simply "the last step that is not locked". The
 * four sales materials unlock TOGETHER the moment the strategy is approved, so
 * a plain furthest-unlocked reading jumps straight to the webinar - the last
 * of a batch nobody has touched - and the ring would lie in the other
 * direction. Measured on the F123 fixture: the plain rule put it on "E-maily"
 * while the sales page was the step being worked on.
 *
 * 🔴 Step 1 is why a finished step cannot drag the ring on its own. A material
 * published before its own step unlocked is the `done_ahead` anomaly (fixr4),
 * and a plain `done` can sit past a locked wall for the same reason. Without
 * the reach, a client sitting on step 1 with three such outputs behind a
 * locked step 2 read as "Krok 12 z 5" - a step they have not opened, and a
 * position past the end of their own rail.
 */
export function staffCurrentStep(
  steps: StaffRailStep[]
): StaffRailStep | null {
  const open = (step: StaffRailStep) => {
    const status = staffStepStatus(step);
    return status !== "locked" && status !== "done" && status !== "done_ahead";
  };
  const advanced = (step: StaffRailStep) => {
    const status = staffStepStatus(step);
    return status === "done" || status === "done_ahead"
      || status === "working" || status === "blocked";
  };
  const finished = (step: StaffRailStep) => {
    const status = staffStepStatus(step);
    return status === "done" || status === "done_ahead";
  };
  let reach = steps.length;
  for (let index = 0; index < steps.length; index += 1) {
    if (staffStepStatus(steps[index]) === "locked") { reach = index; break; }
  }
  // ES2022 target: no Array.prototype.findLast / findLastIndex.
  let frontier = 0;
  for (let index = reach - 1; index >= 0; index -= 1) {
    if (advanced(steps[index])) { frontier = index; break; }
  }
  for (let index = frontier; index < reach; index += 1) {
    if (open(steps[index])) return steps[index];
  }
  // A completed auxiliary step (e.g. Apple account) is not the client's
  // current work while an earlier reached step remains open. Prefer the
  // frontier above when production really advanced; fall back only here.
  for (let index = 0; index < frontier; index += 1) {
    if (open(steps[index])) return steps[index];
  }
  for (let index = reach - 1; index >= 0; index -= 1) {
    if (finished(steps[index])) return steps[index];
  }
  return steps[0] ?? null;
}

/**
 * `total` is REQUIRED on purpose. It used to default to the full 19-step
 * catalog, so a client without the app branch read "Krok 10 z 19" in the
 * status card while their own rail showed 16 steps (audit P0-3). The count
 * always comes from the client's own rail.
 */
export function staffStepTitle(
  step: StaffRailStep,
  t: (key: string) => string,
  total: number,
  doneAhead = 0
): string {
  const position = t("staffConsole.stepPosition.global")
    .replace(
      "{position}",
      String(step.position ?? RAIL_STEP_KEYS.indexOf(step.key) + 1)
    )
    .replace("{total}", String(total));
  const ahead = doneAhead > 0
    ? ` · ${t(`staffConsole.stepPosition.doneAhead.${doneAhead === 1 ? "one" : "other"}`)
        .replace("{count}", String(doneAhead))}`
    : "";
  return `${position}${ahead} · ${t(step.labelKey)}`;
}

export function staffCurrentStepTitle(
  steps: StaffRailStep[],
  t: (key: string) => string
): string {
  const current = staffCurrentStep(steps);
  if (!current) return t("staffConsole.none");
  // ONE definition for "hotové napřed": the canonical done_ahead anomaly and
  // nothing else. Counting every done step past the current position printed
  // "5 hotové napřed" beside a rail that labels three (audit a3-04, a2-11);
  // the title count must equal the number of labelled steps.
  const doneAhead = steps.filter((step) => staffStepStatus(step) === "done_ahead").length;
  return staffStepTitle(current, t, steps.length, doneAhead);
}

export function staffStepStatus(step: StaffRailStep): StaffStepStatus {
  return step.staffStatus ?? step.displayStatus;
}

export function staffStepStatusLabel(
  step: StaffRailStep,
  t: (key: string) => string
): string {
  return t(`rail.status.${staffStepStatus(step)}`);
}

const SECTION_KEYS: SectionKey[] = [
  "home",
  "onboarding_call",
  "q1",
  "knowhow",
  "training",
  "beta",
  "marketing_call",
  "q2",
  "sales",
  "stripe",
  "handover",
  "app"
];

export function orderedSections<T extends { key: SectionKey }>(
  sections: T[]
): T[] {
  return [...sections].sort(
    (a, b) => SECTION_KEYS.indexOf(a.key) - SECTION_KEYS.indexOf(b.key)
  );
}

export function clientInitial(name: string, email: string): string {
  return (name.trim()[0] ?? email.trim()[0] ?? "?").toLocaleUpperCase();
}
